/**
 * Search over the prebuilt index (issue #15, ADR-0008). The index ships with the Snapshot
 * (tools/snapshot/search.ts builds it); here a query is tokenised the same way, every token is
 * a prefix range over the sorted terms, the postings are intersected, and the hits are ranked
 * with exact name matches first. Nothing is indexed on the device.
 */
import type { IndexRecord, SearchIndexFile } from "../../shared/snapshot-format";
import { SEARCH_FORMAT } from "../../shared/snapshot-format";
import { normalizeName, tokenize } from "../../shared/tokenize";
import { loadCatalogue } from "./catalogue";
import { DATA_ROOT, fetchJson, loadManifest } from "./manifest";

export interface SearchHit {
  record: IndexRecord;
  /** The Entry's snippet from the index. */
  snippet: string;
  /** 0 exact name, 1 name prefix, 2 every token a whole word of the name, 3 every token in the name by prefix, 4 some tokens in the name, 5 text only. */
  tier: 0 | 1 | 2 | 3 | 4 | 5;
}

export interface Search {
  /** Ranked hits for a query; empty for a blank query. */
  query(text: string): SearchHit[];
  /** The snippet of a record, "" when the index has none. */
  snippet(record: IndexRecord): string;
}

/** A search over `records` (the catalogue, in index order) backed by the shipped index file. */
export function createSearch(records: readonly IndexRecord[], file: SearchIndexFile): Search {
  if (file.format !== SEARCH_FORMAT) throw new Error(`search index format ${file.format} is not supported by this build (wants ${SEARCH_FORMAT})`);
  if (file.snippets.length !== records.length) throw new Error("the search index does not match the catalogue");
  const ordinals = new Map<string, number>(records.map((r, i) => [r.u, i]));
  const normalised: (string | undefined)[] = new Array<string | undefined>(records.length);
  const nameOf = (i: number): string => (normalised[i] ??= normalizeName(records[i]!.n));
  /** Tier first; within a prefix tier the shorter name; then the normalised name (plain code-point order: fast, and the names are ASCII once normalised). */
  const order = (a: SearchHit, b: SearchHit): number => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.tier <= 1 && a.record.n.length !== b.record.n.length) return a.record.n.length - b.record.n.length;
    const x = nameOf(ordinals.get(a.record.u)!);
    const y = nameOf(ordinals.get(b.record.u)!);
    return x < y ? -1 : x > y ? 1 : 0;
  };

  /** [first, last) of the terms starting with `prefix`. */
  const range = (prefix: string): [number, number] => {
    const terms = file.terms;
    let lo = 0;
    let hi = terms.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (terms[mid]! < prefix) lo = mid + 1;
      else hi = mid;
    }
    let end = lo;
    while (end < terms.length && terms[end]!.startsWith(prefix)) end++;
    return [lo, end];
  };

  return {
    query(text) {
      const tokens = tokenize(text);
      if (tokens.length === 0) {
        // A single character: every name starting with it, so typing shows something at once.
        const c = text.trim().toLowerCase();
        if (!c) return [];
        const out: SearchHit[] = [];
        for (let o = 0; o < records.length; o++) if (nameOf(o).startsWith(c)) out.push({ record: records[o]!, snippet: file.snippets[o]!, tier: 1 });
        return out.sort(order);
      }
      // Per token: which ordinals match through the name, which through anything.
      let any: Map<number, number> | null = null;
      const inName = new Map<number, number>();
      const wholeName = new Map<number, number>();
      for (const token of tokens) {
        const [from, to] = range(token);
        const nameHits = new Set<number>();
        const anyHits = new Set<number>();
        for (let t = from; t < to; t++) {
          for (const o of file.names[t]!) {
            nameHits.add(o);
            anyHits.add(o);
          }
          for (const o of file.texts[t]!) anyHits.add(o);
        }
        for (const o of nameHits) inName.set(o, (inName.get(o) ?? 0) + 1);
        if (from < to && file.terms[from] === token) for (const o of file.names[from]!) wholeName.set(o, (wholeName.get(o) ?? 0) + 1);
        if (any === null) {
          any = new Map();
          for (const o of anyHits) any.set(o, 1);
        } else {
          for (const o of any.keys()) {
            if (anyHits.has(o)) any.set(o, any.get(o)! + 1);
            else any.delete(o);
          }
        }
      }
      const needle = normalizeName(text);
      const hits: SearchHit[] = [];
      for (const [o, count] of any!) {
        if (count < tokens.length) continue;
        const named = inName.get(o) ?? 0;
        const name = nameOf(o);
        const tier: SearchHit["tier"] = name === needle ? 0 : name.startsWith(needle) ? 1 : named === tokens.length ? ((wholeName.get(o) ?? 0) === tokens.length ? 2 : 3) : named > 0 ? 4 : 5;
        hits.push({ record: records[o]!, snippet: file.snippets[o]!, tier });
      }
      return hits.sort(order);
    },
    snippet(record) {
      const o = ordinals.get(record.u);
      return o === undefined ? "" : file.snippets[o]!;
    },
  };
}

let cached: Promise<Search> | undefined;

/** The search over the shipped index, fetched once and kept in memory. */
export function loadSearch(): Promise<Search> {
  cached ??= Promise.all([loadCatalogue(), loadManifest().then((m) => fetchJson<SearchIndexFile>(`${DATA_ROOT}/${m.search.path}`))]).then(([records, file]) => createSearch(records, file));
  return cached;
}
